import { addDays } from 'date-fns';
import { Permission } from '../models/Permissions';
import { AuditEvent, AuditHistoryResponse } from '../models/EventHistoryModels';
import { CloudProvider, DomainStatus, DomainsResponse, TaniumInstance } from '../types';
import { IdpResponse } from '../models/IdpModels';
import { Tenant } from '../models/TenantModels';

export const mockTenant: Tenant = {
  id: '1234',
  workspace: 'customer-identifier',
  region: 'us-e1',
  username: 'testuser@customer-identifier.mytanium.com',
  email: 'testuser@customer-identifier.com',
  verifiedDomains: ['customer-identifier.com'],
};

export const mockEventHistory: AuditHistoryResponse = {
  data: [
    {
      event: AuditEvent.DomainDelete,
      createdAt: addDays(new Date(), -1).getTime(),
      message: 'fake domain delete event',
      details: {},
    },
    {
      event: AuditEvent.DomainCreate,
      createdAt: addDays(new Date(), -2).getTime(),
      message: 'fake domain create event',
      details: {},
    },
    {
      event: AuditEvent.UserUpdate,
      createdAt: addDays(new Date(), -3).getTime(),
      message: 'fake user update event',
      details: {},
    },
    {
      event: AuditEvent.UserCreate,
      createdAt: addDays(new Date(), -4).getTime(),
      message: 'fake user create event',
      details: {},
    },
  ],
  nextPageCursor: '',
  totalCount: 4,
};

export const idpResponses: IdpResponse[] = [
  {
    id: 'idpID',
    name: 'AName',
    displayName: 'AName',
    samlMetadataUrl: '',
    samlMetadataFile: 'fake-metadata-file',
    domainNames: ['ADomain.com'],
    emailAttributeName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
    enableIdpSignout: false,
    enableTaniumConsoleAuth: false,
    enableAccountPortalAuth: true,
  },
  {
    id: 'idpID2',
    name: 'BName',
    displayName: 'BName',
    samlMetadataUrl:
      'https://login.microsoftonline.com/c48fd9e4-a55a-46b9-a824-c8f1daa22b1c/federationmetadata/2007-06/federationmetadata.xml?appid=b8360aa1-c041-41f8-b891-4df854f73b32',
    samlMetadataFile: '',
    domainNames: ['BDomain.com', 'test.com'],
    emailAttributeName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
    enableIdpSignout: true,
    enableTaniumConsoleAuth: false,
    enableAccountPortalAuth: true,
  },
  {
    id: 'idpID3',
    name: 'CName',
    displayName: 'CName',
    samlMetadataUrl: '',
    samlMetadataFile: 'fake-metadata-file',
    domainNames: ['CDomain.com'],
    emailAttributeName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
    enableIdpSignout: false,
    enableTaniumConsoleAuth: false,
    enableAccountPortalAuth: true,
  },
];

export const createIdpResponse: IdpResponse = {
  id: 'idpID',
  name: 'idpName',
  displayName: 'idpDisplayName',
  emailAttributeName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
  samlMetadataUrl:
    'https://login.microsoftonline.com/c48fd9e4-a55a-46b9-a824-c8f1daa22b1c/federationmetadata/2007-06/federationmetadata.xml?appid=b8360aa1-c041-41f8-b891-4df854f73b32',
  samlMetadataFile: '',
  enableTaniumConsoleAuth: false,
  enableAccountPortalAuth: true,
  enableIdpSignout: true,
  domainNames: ['example.com', 'test.com'],
};

export const mockVerificationDomains: DomainsResponse = {
  data: [
    {
      id: '34ed74a8-5f1e-4af9-a37d-143a08b5c587',
      tenantId: '86c365b4-15ff-4ed2-89dc-b6f23555bc9c',
      domainName: 'test.com',
      dnsRecordValue: '36fe50d1-d5fa-4baa-8b2c-aa69c0269051',
      verificationStatus: DomainStatus.PENDING,
      domainUsages: [],
    },
    {
      id: '9dk39d8-5f1e-4af9-a37d-143a08b5c587',
      tenantId: '0d93k4-15ff-4ed2-89dc-b6f23555bc9c',
      domainName: 'mydomain.com',
      dnsRecordValue: '9267e2a7-045b-4d88-851c-39f4f29ba933',
      verificationStatus: DomainStatus.SUCCESS,
      domainUsages: ['myIDP'],
    },
    {
      id: 'f455ccb7-45c8-4c6d-8131-d6037694e79e',
      tenantId: '0d93k4-15ff-4ed2-89dc-b6f23555bc9c',
      domainName: 'success.com',
      dnsRecordValue: '8a443c11-9588-41bd-aed6-dba90905f444',
      verificationStatus: DomainStatus.SUCCESS,
      domainUsages: [],
    },
    {
      id: '909eeed9-ac3e-4f2d-9591-ecb77267ee3a',
      tenantId: '0d93k4-15ff-4ed2-89dc-b6f23555bc9c',
      domainName: 'mydomainerror.com',
      dnsRecordValue: '3eae906a-0ee8-4c87-b5ab-c8883c2018dc',
      verificationStatus: DomainStatus.ERROR,
      domainUsages: [],
    },
    {
      id: '8f2b76d3-9d99-4d97-85ae-d7984a34fe35',
      tenantId: '0d93k4-15ff-4ed2-89dc-b6f23555bc9c',
      domainName: 'mydomainbadstatus.com',
      dnsRecordValue: '16031808-5080-4208-a2d7-c3ae154e30a7',
      verificationStatus: 'BAD STATUS' as DomainStatus,
      domainUsages: [],
    },
    {
      id: '2m2ux4m2-1fnz-23nd-m3no-ab92nxm24mn5',
      tenantId: '9x3nm1-m241-4bx2-789x-a24mbn2khx4',
      domainName: 'mydomainnodomain.com',
      dnsRecordValue: '4829x14a-1c3z-1m3n-2hck-3nchx1231c2',
      verificationStatus: DomainStatus.SUCCESS,
      domainUsages: ['myIDP'],
    },
  ],
  nextPageCursor: null,
  totalCount: 6,
};

export const mockTaniumInstances: TaniumInstance[] = [
  {
    id: '600f87db-fd43-4e31-a01f-bcdb55f6a0d9',
    licenseId: '25415',
    createdAt: '2024-01-01',
    displayName: 'name 1',
    cloudProvider: CloudProvider.AZURE,
  },
  {
    id: '9dk39d8-5f1e-4af9-a37d-143a08b5c587',
    licenseId: '11875',
    createdAt: new Date().toISOString(),
    displayName: 'name 2',
    cloudProvider: CloudProvider.AWS,
  },
  {
    id: 'prd-123',
    licenseId: '235152',
    createdAt: '2023-01-01',
    displayName: 'On-prem instance 3',
  },
];

export const mockPermissions = Object.values(Permission);
